import React, { useEffect, useState } from 'react';
import { CalendarIcon, DollarSignIcon, RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon } from 'lucide-react';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  // Payment details
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('MYR');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  // Subscription details
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState('');
  const [trialDays, setTrialDays] = useState('0');
  // Customer selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    name: string;
    email: string;
    cards: Array<{
      id: string;
      last4: string;
      brand: string;
      expiry: string;
    }>;
  } | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  // Subscription mode
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  // Customer email/phone for new subscription
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Notify parent component when subscription mode changes
  useEffect(() => {
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(subscriptionMode);
    }
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Mock customer data
  const customers = [{
    id: 'cust_1',
    name: 'John Doe',
    email: 'john@example.com',
    cards: [{
      id: 'card_1',
      last4: '4242',
      brand: 'Visa',
      expiry: '04/25'
    }, {
      id: 'card_2',
      last4: '5555',
      brand: 'Mastercard',
      expiry: '07/24'
    }]
  }, {
    id: 'cust_2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    cards: [{
      id: 'card_3',
      last4: '1234',
      brand: 'Visa',
      expiry: '09/26'
    }]
  }];
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    setAmount(value);
    onAmountChange(value);
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrency(e.target.value);
    onCurrencyChange(e.target.value);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelectedCustomer(null);
    setSelectedCard(null);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
  };
  const handleSelectCard = (cardId: string) => {
    setSelectedCard(cardId);
  };
  const handleSubscriptionModeChange = (mode: 'existing' | 'new') => {
    setSubscriptionMode(mode);
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(mode);
    }
  };
  // Filter customers based on search term
  const filteredCustomers = searchTerm ? customers.filter(customer => customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  return <div className="space-y-6" data-id="element-2738">
      {/* Subscription Mode Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2739">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2740">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-2741" />
          Subscription Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2742">
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('existing')} data-id="element-2743">
            <div className="flex items-start space-x-3" data-id="element-2744">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-2745">
                <CreditCardIcon size={20} className="text-blue-600" data-id="element-2746" />
              </div>
              <div data-id="element-2747">
                <h3 className="font-medium text-gray-900" data-id="element-2748">Existing Customer</h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2749">
                  Create a subscription for an existing customer with a saved
                  payment method
                </p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('new')} data-id="element-2750">
            <div className="flex items-start space-x-3" data-id="element-2751">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-2752">
                <LinkIcon size={20} className="text-blue-600" data-id="element-2753" />
              </div>
              <div data-id="element-2754">
                <h3 className="font-medium text-gray-900" data-id="element-2755">
                  New Subscription Link
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2756">
                  Send a link to customer to start a new subscription
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Customer Selection (for existing customers) */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2757">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2758">
            Customer Selection
          </h3>
          <div className="space-y-4" data-id="element-2759">
            <div data-id="element-2760">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2761">
                Search Customer
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2762">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2763">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-2764" />
                </div>
                <input type="text" value={searchTerm} onChange={handleSearchChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name or email" data-id="element-2765" />
              </div>
            </div>
            {searchTerm && filteredCustomers.length > 0 && !selectedCustomer && <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-2766">
                  <ul className="divide-y divide-gray-200" data-id="element-2767">
                    {filteredCustomers.map(customer => <li key={customer.id} className="hover:bg-gray-50" data-id="element-2768">
                        <button type="button" onClick={() => handleSelectCustomer(customer)} className="w-full px-4 py-3 text-left flex items-center" data-id="element-2769">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-2770">
                            <UserIcon size={16} data-id="element-2771" />
                          </div>
                          <div data-id="element-2772">
                            <p className="text-sm font-medium text-gray-900" data-id="element-2773">
                              {customer.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2774">
                              {customer.email}
                            </p>
                          </div>
                        </button>
                      </li>)}
                  </ul>
                </div>}
            {selectedCustomer && <div className="bg-gray-50 rounded-md p-4" data-id="element-2775">
                <div className="flex items-center justify-between" data-id="element-2776">
                  <div className="flex items-center" data-id="element-2777">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-2778">
                      <UserIcon size={20} data-id="element-2779" />
                    </div>
                    <div data-id="element-2780">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2781">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2782">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setSelectedCustomer(null)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2783">
                    Change
                  </button>
                </div>
                <div className="mt-4" data-id="element-2784">
                  <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-2785">
                    Saved Payment Methods
                  </p>
                  <div className="space-y-2" data-id="element-2786">
                    {selectedCustomer.cards.map(card => <div key={card.id} onClick={() => handleSelectCard(card.id)} className={`border rounded-md p-3 cursor-pointer ${selectedCard === card.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'}`} data-id="element-2787">
                        <div className="flex items-center justify-between" data-id="element-2788">
                          <div className="flex items-center" data-id="element-2789">
                            <div className="mr-3" data-id="element-2790">
                              {card.brand === 'Visa' ? <CreditCardIcon size={20} className="text-blue-700" data-id="element-2791" /> : card.brand === 'Mastercard' ? <CreditCardIcon size={20} className="text-red-600" data-id="element-2792" /> : <CreditCardIcon size={20} className="text-gray-600" data-id="element-2793" />}
                            </div>
                            <div data-id="element-2794">
                              <p className="text-sm font-medium text-gray-900" data-id="element-2795">
                                {card.brand} •••• {card.last4}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-2796">
                                Expires {card.expiry}
                              </p>
                            </div>
                          </div>
                          <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-2797">
                            {selectedCard === card.id && <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-2798"></div>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>}
      {/* Customer Information (for new subscription) */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2799">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2800">
            Customer Information
          </h3>
          <div className="space-y-4" data-id="element-2801">
            <div data-id="element-2802">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2803">
                Customer Name (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2804">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2805">
                  <UserIcon size={16} className="text-gray-400" data-id="element-2806" />
                </div>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="John Doe" data-id="element-2807" />
              </div>
            </div>
            <div data-id="element-2808">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2809">
                Customer Email
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2810">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2811">
                  <MailIcon size={16} className="text-gray-400" data-id="element-2812" />
                </div>
                <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customer@example.com" required data-id="element-2813" />
              </div>
            </div>
            <div data-id="element-2814">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2815">
                Customer Phone (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2816">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2817">
                  <PhoneIcon size={16} className="text-gray-400" data-id="element-2818" />
                </div>
                <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="+1 (555) 123-4567" data-id="element-2819" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-2820">
                We'll send a subscription link to the customer via email or SMS
              </p>
            </div>
          </div>
        </div>}
      {/* Subscription Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2821">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2822">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-2823" />
          Subscription Details
        </h3>
        <div className="space-y-4" data-id="element-2824">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2825">
            <div data-id="element-2826">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2827">
                Amount per Billing Period*
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2828">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2829">
                  <span className="text-gray-500" data-id="element-2830">RM</span>
                </div>
                <input type="text" value={amount} onChange={handleAmountChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" required data-id="element-2831" />
              </div>
            </div>
            <div data-id="element-2832">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2833">
                Currency
              </label>
              <select value={currency} onChange={handleCurrencyChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2834">
                <option value="MYR" data-id="element-2835">MYR - Malaysian Ringgit</option>
                <option value="USD" data-id="element-2836">USD - US Dollar</option>
                <option value="EUR" data-id="element-2837">EUR - Euro</option>
                <option value="GBP" data-id="element-2838">GBP - British Pound</option>
                <option value="JPY" data-id="element-2839">JPY - Japanese Yen</option>
                <option value="CAD" data-id="element-2840">CAD - Canadian Dollar</option>
                <option value="AUD" data-id="element-2841">AUD - Australian Dollar</option>
              </select>
            </div>
          </div>
          <div data-id="element-2842">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2843">
              Description*
            </label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Premium Membership" required data-id="element-2844" />
          </div>
          <div data-id="element-2845">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2846">
              Your Reference (Optional)
            </label>
            <input type="text" value={reference} onChange={handleReferenceChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Internal reference" data-id="element-2847" />
          </div>
        </div>
      </div>
      {/* Billing Schedule */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2848">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2849">
          <ClockIcon size={20} className="mr-2 text-blue-600" data-id="element-2850" />
          Billing Schedule
        </h3>
        <div className="space-y-4" data-id="element-2851">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2852">
            <div data-id="element-2853">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2854">
                Billing Interval
              </label>
              <select value={billingInterval} onChange={e => setBillingInterval(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2855">
                <option value="weekly" data-id="element-2856">Weekly</option>
                <option value="biweekly" data-id="element-2857">Every 2 Weeks</option>
                <option value="monthly" data-id="element-2858">Monthly</option>
                <option value="quarterly" data-id="element-2859">Quarterly</option>
                <option value="biannually" data-id="element-2860">Every 6 Months</option>
                <option value="annually" data-id="element-2861">Annually</option>
              </select>
            </div>
            <div data-id="element-2862">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2863">
                Number of Billing Cycles (Optional)
              </label>
              <input type="number" min="1" value={billingCycles} onChange={e => setBillingCycles(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Leave blank for unlimited" data-id="element-2864" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-2865">
                Leave blank for an ongoing subscription
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2866">
            <div data-id="element-2867">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2868">
                Start Date
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2869">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2870">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-2871" />
                </div>
                <input type="text" value={startDate} onChange={e => setStartDate(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" data-id="element-2872" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-2873">
                Leave blank to start immediately
              </p>
            </div>
            <div data-id="element-2874">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2875">
                Trial Period (Days)
              </label>
              <input type="number" min="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-2876" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-2877">
                Free trial before first payment
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2878">
        <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-2879">
          Subscription Summary
        </h3>
        <div className="space-y-2" data-id="element-2880">
          <div className="flex justify-between" data-id="element-2881">
            <span className="text-sm text-gray-600" data-id="element-2882">Amount:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2883">
              {amount ? `RM ${amount} ${currency}` : '-'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-2884">
            <span className="text-sm text-gray-600" data-id="element-2885">Billing Interval:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2886">
              {billingInterval === 'weekly' ? 'Weekly' : billingInterval === 'biweekly' ? 'Every 2 Weeks' : billingInterval === 'monthly' ? 'Monthly' : billingInterval === 'quarterly' ? 'Every 3 Months' : billingInterval === 'biannually' ? 'Every 6 Months' : 'Annually'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-2887">
            <span className="text-sm text-gray-600" data-id="element-2888">Duration:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2889">
              {billingCycles ? `${billingCycles} billing cycles` : 'Ongoing'}
            </span>
          </div>
          {parseInt(trialDays) > 0 && <div className="flex justify-between" data-id="element-2890">
              <span className="text-sm text-gray-600" data-id="element-2891">Trial Period:</span>
              <span className="text-sm font-medium text-gray-800" data-id="element-2892">
                {trialDays} days
              </span>
            </div>}
          <div className="flex justify-between" data-id="element-2893">
            <span className="text-sm text-gray-600" data-id="element-2894">Start Date:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2895">
              {startDate || 'Immediate'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-2896">
            <span className="text-sm text-gray-600" data-id="element-2897">Customer:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2898">
              {subscriptionMode === 'existing' ? selectedCustomer ? selectedCustomer.name : 'Not selected' : customerEmail ? customerName || customerEmail : 'Not specified'}
            </span>
          </div>
          {subscriptionMode === 'existing' && selectedCustomer && selectedCard && <div className="flex justify-between" data-id="element-2899">
                <span className="text-sm text-gray-600" data-id="element-2900">Payment Method:</span>
                <span className="text-sm font-medium text-gray-800" data-id="element-2901">
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.brand}{' '}
                  ••••
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.last4}
                </span>
              </div>}
        </div>
      </div>
    </div>;
}